import { keepPreviousData, useQuery, useMutation, useQueryClient } from '@tanstack/react-query'

import { 
  searchRecords,
  getRecordSummary,
  getRecord, 
  clickRecordButton,
  createRecord, 
  deleteRecord, 
  updateRecordValue,
  updateRecord,
  getTrashedRecords,
  getRecordTrashStats,
  restoreRecord,
  permanentlyDeleteRecord,
} from '@/services/api/record.service'
import type { RecordSearchRequest, UpdateRecordValueRequest, UpdateRecordRequest } from '@/types/api/record'

import { useWorkspace } from './useWorkspace'

/** Record mutations scoped to a group; they refresh every list and summary of that group. */
export function useRecords(recordGroupId: string) {
  const { currentWorkspaceId } = useWorkspace()
  const queryClient = useQueryClient()
  const recordsKey = ['workspace', currentWorkspaceId, 'record-groups', recordGroupId, 'records']

  const createMutation = useMutation({
    mutationFn: createRecord,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: recordsKey })
      // Group lists show record counts
      queryClient.invalidateQueries({ queryKey: ['workspace', currentWorkspaceId, 'record-groups', 'search'] })
    },
  })

  const deleteMutation = useMutation({
    mutationFn: deleteRecord,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: recordsKey })
      queryClient.invalidateQueries({ queryKey: ['workspace', currentWorkspaceId, 'record-groups', 'search'] })
    },
  })

  return {
    create: createMutation.mutate,
    delete: deleteMutation.mutate,
    isCreating: createMutation.isPending,
    isDeleting: deleteMutation.isPending,
  }
}

/** One page of a group's records with stacked filters; keeps the previous page visible while loading. */
export function useRecordSearch(recordGroupId: string | undefined, request: RecordSearchRequest) {
  const { currentWorkspaceId } = useWorkspace()
  return useQuery({
    queryKey: ['workspace', currentWorkspaceId, 'record-groups', recordGroupId, 'records', 'search', request],
    queryFn: () => searchRecords(recordGroupId!, request),
    enabled: !!recordGroupId && !!currentWorkspaceId,
    placeholderData: keepPreviousData,
    staleTime: 30 * 1000,
  })
}

/** Overview counts of the records the user may see in a group. */
export function useRecordSummary(recordGroupId: string | undefined, includeSubgroups = false) {
  const { currentWorkspaceId } = useWorkspace()
  return useQuery({
    queryKey: ['workspace', currentWorkspaceId, 'record-groups', recordGroupId, 'records', 'summary', includeSubgroups],
    queryFn: () => getRecordSummary(recordGroupId!, includeSubgroups),
    enabled: !!recordGroupId && !!currentWorkspaceId,
    staleTime: 30 * 1000,
  })
}

export function useRecordDetail(recordId: string) {
  const { currentWorkspaceId } = useWorkspace()
  const queryClient = useQueryClient()

  const query = useQuery({
    queryKey: ['workspace', currentWorkspaceId, 'records', recordId],
    queryFn: () => getRecord(recordId),
    enabled: !!recordId && !!currentWorkspaceId,
  })

  const updateValueMutation = useMutation({
    mutationFn: ({ recordGroupId, data }: { recordGroupId: string; data: UpdateRecordValueRequest }) =>
      updateRecordValue(recordGroupId, data),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ['workspace', currentWorkspaceId, 'records', recordId],
      })
    },
  })

  const updateMutation = useMutation({
    mutationFn: ({ recordId: rid, data }: { recordId: string; data: UpdateRecordRequest }) =>
      updateRecord(rid, data),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ['workspace', currentWorkspaceId, 'records', recordId],
      })
    },
  })

  const deleteMutation = useMutation({
    mutationFn: deleteRecord,
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ['workspace', currentWorkspaceId, 'record-groups'],
      })
    },
  })

  // A click is a timeline entry and may start automations that write notes or change fields
  const clickButtonMutation = useMutation({
    mutationFn: (fieldKey: string) => clickRecordButton(recordId, fieldKey),
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: ['workspace', currentWorkspaceId, 'records', recordId] })
      queryClient.invalidateQueries({ queryKey: ['activities', recordId] })
    },
  })

  return {
    record: query.data,
    clickButtonAsync: clickButtonMutation.mutateAsync,
    isClickingButton: clickButtonMutation.isPending,
    isLoading: query.isLoading,
    error: query.error,
    updateValue: updateValueMutation.mutate,
    updateValueAsync: updateValueMutation.mutateAsync,
    isUpdatingValue: updateValueMutation.isPending,
    updateValueError: updateValueMutation.error,
    update: updateMutation.mutate,
    updateAsync: updateMutation.mutateAsync,
    isUpdating: updateMutation.isPending,
    updateError: updateMutation.error,
    delete: deleteMutation.mutate,
    isDeleting: deleteMutation.isPending,
    deleteError: deleteMutation.error,
  }
}

export function useRecordTrash(pageNumber: number, pageSize: number) {
  const { currentWorkspaceId } = useWorkspace()
  const queryClient = useQueryClient()

  const query = useQuery({
    queryKey: ['workspace', currentWorkspaceId, 'records-trash', pageNumber, pageSize],
    queryFn: () => getTrashedRecords(pageNumber, pageSize),
    enabled: !!currentWorkspaceId,
    staleTime: 30 * 1000,
  })

  const statsQuery = useQuery({
    queryKey: ['workspace', currentWorkspaceId, 'records-trash-stats'],
    queryFn: getRecordTrashStats,
    enabled: !!currentWorkspaceId,
    staleTime: 60 * 1000,
  })

  const restoreMutation = useMutation({
    mutationFn: restoreRecord,
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ['workspace', currentWorkspaceId, 'records-trash'],
      })
      queryClient.invalidateQueries({
        queryKey: ['workspace', currentWorkspaceId, 'records-trash-stats'],
      })
    },
  })

  const permanentlyDeleteMutation = useMutation({
    mutationFn: permanentlyDeleteRecord,
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ['workspace', currentWorkspaceId, 'records-trash'],
      })
      queryClient.invalidateQueries({
        queryKey: ['workspace', currentWorkspaceId, 'records-trash-stats'],
      })
    },
  })

  return {
    trashedRecords: query.data?.content ?? [],
    pagination: {
      totalElements: query.data?.totalElements ?? 0,
      totalPages: query.data?.totalPages ?? 0,
      currentPage: query.data?.number ?? 0,
      pageSize: query.data?.size ?? 10,
      isFirst: query.data?.first ?? true,
      isLast: query.data?.last ?? true,
      isEmpty: query.data?.empty ?? true,
    },
    isLoading: query.isLoading,
    error: query.error,
    stats: statsQuery.data,
    statsIsLoading: statsQuery.isLoading,
    restore: restoreMutation.mutate,
    permanentlyDelete: permanentlyDeleteMutation.mutate,
    isRestoring: restoreMutation.isPending,
    isPermanentlyDeleting: permanentlyDeleteMutation.isPending,
    restoreError: restoreMutation.error,
    permanentlyDeleteError: permanentlyDeleteMutation.error,
  }
}
